// src/services/GraphBuilderService.ts
import type { AncestorGraph } from '../domain/AncestorGraph';
import type { Person } from '../domain/Person';
import type { RawDataset } from '../domain/RawDataset';
import { createLogger } from '../utils/logger';
import { wikiTreeIdKey } from '../utils/WikiTreeId';

const logger = createLogger('GraphBuilderService');

export class GraphBuilderService {
  /**
   * Walk ancestor links from rootId via BFS, building a deduplicated AncestorGraph.
   *
   * Pedigree collapse: a person reached via multiple paths appears exactly once
   * (at the generation of the first/shortest path — BFS natural behavior).
   *
   * Brick walls: persons with no known parents in the full dataset are marked
   * isBrickWall=true. Persons excluded by the generation cap are NOT brick walls
   * (their fatherId/motherId remain defined).
   *
   * Phase 2 note: this method mutates person.isBrickWall on shared RawDataset
   * Person objects. If buildGraph is called again on an expanded dataset (via
   * IAncestorDataSource.expandFrom), callers must reset isBrickWall=false on all
   * persons before re-calling. Deferred decision — see P-04 doc § 4.2.
   */
  buildGraph(dataset: RawDataset, rootId: string, maxGenerations = 12): AncestorGraph {
    const rootPerson = dataset.persons.get(rootId);
    if (rootPerson === undefined) {
      logger.error(`buildGraph called with unknown rootId: ${rootId}`);
      throw new Error(`rootId not found in dataset: ${rootId}`);
    }

    logger.info(`buildGraph started — rootId=${rootId}, maxGenerations=${maxGenerations}`);

    const persons = new Map<string, Person>();
    const queue: Array<{ personId: string; generation: number }> = [
      { personId: rootId, generation: 0 },
    ];
    let capWarned = false;

    while (queue.length > 0) {
      const entry = queue.shift();
      if (entry === undefined) break;
      const { personId, generation } = entry;

      if (persons.has(personId)) continue; // pedigree-collapse dedup

      if (generation > maxGenerations) {
        if (!capWarned) {
          logger.warn(
            `buildGraph: generation cap (${maxGenerations}) reached — some ancestors excluded`,
          );
          capWarned = true;
        }
        continue;
      }

      const person = dataset.persons.get(personId);
      if (person === undefined) continue;

      persons.set(personId, person);

      // Enqueue father first, then mother — deterministic walk order for test stability.
      if (person.fatherId !== undefined && dataset.persons.has(person.fatherId)) {
        queue.push({ personId: person.fatherId, generation: generation + 1 });
      }
      if (person.motherId !== undefined && dataset.persons.has(person.motherId)) {
        queue.push({ personId: person.motherId, generation: generation + 1 });
      }
    }

    // Mark brick walls: no known parents in the full dataset.
    // Cap-boundary persons have fatherId/motherId defined — they are NOT brick walls.
    for (const person of persons.values()) {
      const hasFather = person.fatherId !== undefined && dataset.persons.has(person.fatherId);
      const hasMother = person.motherId !== undefined && dataset.persons.has(person.motherId);
      if (!hasFather && !hasMother) {
        person.isBrickWall = true;
      }
    }

    // Keep only ancestor marriages (P-15e): a marriage whose family produced a person in this
    // graph. Other marriages — to non-ancestors, and the root person's own — fall outside
    // every ancestor's timeline. Marriages with no family id (sources without family
    // records) are kept. Changed persons are copied, so the dataset is not modified.
    const ancestorFamilies = new Set<string>();
    for (const person of persons.values()) {
      if (person.birthFamilyId !== undefined) {
        ancestorFamilies.add(person.birthFamilyId);
      }
    }
    for (const [id, person] of persons) {
      const events = person.events.filter(
        (e) =>
          e.type !== 'marriage' || e.familyId === undefined || ancestorFamilies.has(e.familyId),
      );
      if (events.length !== person.events.length) {
        persons.set(id, { ...person, events });
      }
    }

    // Carry forward only child-birth evidence for ancestors in this graph.
    const childBirthsByParent = new Map(
      [...dataset.childBirthsByParent.entries()].filter(([key]) => persons.has(key)),
    );

    const brickWallCount = [...persons.values()].filter((p) => p.isBrickWall).length;
    logger.info(`buildGraph complete — ${persons.size} persons, ${brickWallCount} brick walls`);

    return { rootId, persons, childBirthsByParent };
  }

  /**
   * Resolve a user-supplied string to an internal person id.
   *
   * Resolution order:
   *   1. Empty/blank input → first INDI record (WikiTree export convention)
   *   2. Exact match against person.id (trimmed input)
   *   3. Match against person.wikitreeId — trimmed, case-insensitive, "_" ≡ " "
   *   4. null — caller must surface a user-facing "not found" error (P-11)
   */
  static resolveRootId(dataset: RawDataset, input: string): string | null {
    const trimmed = input.trim();
    if (trimmed === '') {
      const first = dataset.persons.keys().next();
      return first.done ? null : first.value;
    }
    if (dataset.persons.has(trimmed)) {
      return trimmed;
    }
    const key = wikiTreeIdKey(trimmed);
    for (const person of dataset.persons.values()) {
      if (person.wikitreeId !== undefined && wikiTreeIdKey(person.wikitreeId) === key) {
        return person.id;
      }
    }
    return null;
  }
}
